import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { entities } from "@/api/apiClient";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileText,
  Plus,
  Search,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Calendar,
  Building2,
  Filter,
} from "lucide-react";
import { format, differenceInDays, addMonths } from "date-fns";
import { safeParseISO } from "@/utils";
import { ptBR } from "date-fns/locale";

export default function ContratosPage() {
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroAlerta, setFiltroAlerta] = useState("todos");

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["contratos"],
    queryFn: () => entities.Contrato.list(),
    retry: 2,
  });
  const contratos = Array.isArray(data) ? data : [];

  const { data: aditivosData } = useQuery({
    queryKey: ["aditivos"],
    queryFn: () => entities.Aditivo.list().catch(() => []),
  });
  const aditivos = Array.isArray(aditivosData) ? aditivosData : [];

  const getStatusConfig = (status) => {
    const configs = {
      Rascunho: { color: "bg-gray-100 text-gray-800", icon: Clock },
      "Em Validação": { color: "bg-yellow-100 text-yellow-800", icon: Clock },
      Ativo: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      Suspenso: { color: "bg-orange-100 text-orange-800", icon: AlertCircle },
      Encerrado: { color: "bg-blue-100 text-blue-800", icon: CheckCircle },
      Rescindido: { color: "bg-red-100 text-red-800", icon: AlertCircle },
    };
    return configs[status] || configs.Rascunho;
  };

  const c = (contrato, key) => contrato[key] ?? contrato[key.replace(/_([a-z])/g, (_, l) => l.toUpperCase())];

  const calcularAlertaVencimento = (contrato) => {
    if (contrato.status === "Encerrado" || contrato.status === "Rescindido") return null;
    const dataFimVal = c(contrato, 'data_fim');
    const hoje = new Date();
    const dataFim = safeParseISO(dataFimVal);
    if (!dataFim) return null;
    const diasRestantes = differenceInDays(dataFim, hoje);

    if (diasRestantes < 0) {
      return { nivel: "vencido", cor: "red", texto: "Vencido", dias: Math.abs(diasRestantes) };
    }

    const raw = contrato.alertasVencimento ?? contrato.alertas_vencimento;
    const alertas = Array.isArray(raw) ? raw : [12, 6, 3, 1];
    for (const meses of alertas.sort((a, b) => a - b)) {
      const dataAlerta = addMonths(dataFim, -meses);
      if (hoje >= dataAlerta) {
        return {
          nivel: meses <= 1 ? "critico" : meses <= 3 ? "atencao" : "aviso",
          cor: meses <= 1 ? "red" : meses <= 3 ? "orange" : "yellow",
          texto: `${meses} ${meses === 1 ? "mês" : "meses"}`,
          dias: diasRestantes,
        };
      }
    }

    return { nivel: "normal", cor: "green", texto: "Normal", dias: diasRestantes };
  };

  const contratosFiltrados = useMemo(() => {
    return contratos.filter((contrato) => {
      const buscaLower = busca.toLowerCase();
      const matchBusca =
        !busca ||
        (contrato.numeroContrato ?? contrato.numero_contrato ?? '')?.toLowerCase().includes(buscaLower) ||
        (contrato.orgaoContratante ?? contrato.orgao_contratante ?? '')?.toLowerCase().includes(buscaLower) ||
        (contrato.fornecedor ?? '')?.toLowerCase().includes(buscaLower) ||
        (contrato.objeto ?? '')?.toLowerCase().includes(buscaLower);

      const matchStatus = filtroStatus === "todos" || contrato.status === filtroStatus;

      const alerta = calcularAlertaVencimento(contrato);
      const matchAlerta =
        filtroAlerta === "todos" ||
        (filtroAlerta === "vencidos" && alerta?.nivel === "vencido") ||
        (filtroAlerta === "criticos" && alerta?.nivel === "critico") ||
        (filtroAlerta === "atencao" && (alerta?.nivel === "atencao" || alerta?.nivel === "critico"));

      return matchBusca && matchStatus && matchAlerta;
    });
  }, [contratos, busca, filtroStatus, filtroAlerta]);

  const estatisticas = useMemo(() => {
    const ativos = contratos.filter((c) => c.status === "Ativo");
    const valorTotal = ativos.reduce((sum, c) => sum + (c.valor_total || 0), 0);
    const valorFaturado = ativos.reduce((sum, c) => sum + (c.valor_faturado || 0), 0);
    const valorGlosado = ativos.reduce((sum, c) => sum + (c.valor_glosado || 0), 0);
    const valorAFaturar = valorTotal - valorFaturado;

    const alertasVencimento = ativos.map(calcularAlertaVencimento);
    const vencidos = alertasVencimento.filter((a) => a?.nivel === "vencido").length;
    const criticos = alertasVencimento.filter((a) => a?.nivel === "critico").length;

    return {
      total: contratos.length,
      ativos: ativos.length,
      valorTotal,
      valorFaturado,
      valorGlosado,
      valorAFaturar,
      vencidos,
      criticos,
    };
  }, [contratos]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6 flex items-center justify-center">
        <Card className="max-w-md border-amber-200 bg-amber-50/80">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-amber-600 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-amber-900 mb-2">Não foi possível carregar os contratos</h2>
            <p className="text-sm text-amber-800 mb-4">
              Verifique sua conexão. Se o problema continuar, o servidor pode estar indisponível.
            </p>
            {error?.message && (
              <p className="text-xs text-amber-700 mb-4 font-mono">{error.message}</p>
            )}
            <Button onClick={() => refetch()} variant="outline" className="border-amber-600 text-amber-800">
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6" data-tour="tour-Contratos-root">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              Contratos
            </h1>
            <p className="text-slate-600 mt-1">Gestão completa de contratos e aditivos</p>
            {isLoading && (
              <p className="text-xs text-slate-500 mt-1">
                Carregando contratos...
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Link to={createPageUrl("ForecastContratos")}>
              <Button variant="outline" className="gap-2">
                <TrendingUp className="w-4 h-4" />
                Forecast Financeiro
              </Button>
            </Link>
            <Link to={createPageUrl("CriarContrato")} data-tour="tour-Contratos-primary">
              <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                <Plus className="w-4 h-4" />
                Novo Contrato
              </Button>
            </Link>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total de Contratos</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{estatisticas.total}</p>
                  <p className="text-xs text-green-600 mt-1">{estatisticas.ativos} ativos</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Valor Total Contratado</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {formatCurrency(estatisticas.valorTotal)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Contratos ativos</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Valor a Faturar</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {formatCurrency(estatisticas.valorAFaturar)}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Faturado: {formatCurrency(estatisticas.valorFaturado)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Alertas de Vencimento</p>
                  <p className="text-3xl font-bold text-orange-600 mt-1">
                    {estatisticas.vencidos + estatisticas.criticos}
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    {estatisticas.vencidos} vencidos, {estatisticas.criticos} críticos
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros */}
        <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por número, órgão, fornecedor ou objeto..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os Status</SelectItem>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Em Validação">Em Validação</SelectItem>
                  <SelectItem value="Rascunho">Rascunho</SelectItem>
                  <SelectItem value="Suspenso">Suspenso</SelectItem>
                  <SelectItem value="Encerrado">Encerrado</SelectItem>
                  <SelectItem value="Rescindido">Rescindido</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filtroAlerta} onValueChange={setFiltroAlerta}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por alerta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="vencidos">Vencidos</SelectItem>
                  <SelectItem value="criticos">Críticos (até 1 mês)</SelectItem>
                  <SelectItem value="atencao">Atenção (até 3 meses)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Contratos */}
        <div className="space-y-4">
          {contratosFiltrados.length === 0 ? (
            <Card className="border-none shadow-lg bg-white/90 backdrop-blur-sm">
              <CardContent className="p-12 text-center">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Nenhum contrato encontrado
                </h3>
                <p className="text-slate-600 mb-6">
                  {busca || filtroStatus !== "todos" || filtroAlerta !== "todos"
                    ? "Tente ajustar os filtros de busca"
                    : "Comece criando seu primeiro contrato"}
                </p>
                {!busca && filtroStatus === "todos" && filtroAlerta === "todos" && (
                  <Link to={createPageUrl("CriarContrato")}>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Criar Primeiro Contrato
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          ) : (
            contratosFiltrados
              .filter((c) => c != null && c.id != null)
              .map((contrato) => {
              const statusConfig = getStatusConfig(contrato.status ?? 'Rascunho');
              const alerta = calcularAlertaVencimento(contrato);
              const numAditivos = aditivos.filter((a) => (a.contratoId ?? a.contrato_id) === contrato.id).length;

              return (
                <Card
                  key={contrato.id}
                  className="border-none shadow-lg bg-white/90 backdrop-blur-sm hover:shadow-xl transition-shadow"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Link to={createPageUrl(`VisualizarContrato?id=${contrato.id ?? ''}`)}>
                                <h3 className="text-lg font-semibold text-slate-900 hover:text-blue-600 transition-colors">
                                  {contrato.numeroContrato ?? contrato.numero_contrato ?? '-'}
                                </h3>
                              </Link>
                              <Badge className={statusConfig.color}>
                                {contrato.status ?? 'Rascunho'}
                              </Badge>
                              {alerta && alerta.nivel !== "normal" && (
                                <Badge
                                  className={`bg-${alerta.cor}-100 text-${alerta.cor}-800`}
                                >
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  {alerta.nivel === "vencido"
                                    ? `Vencido há ${alerta.dias} dias`
                                    : `Vence em ${alerta.dias} dias`}
                                </Badge>
                              )}
                              {numAditivos > 0 && (
                                <Badge variant="outline">
                                  {numAditivos} {numAditivos === 1 ? "Aditivo" : "Aditivos"}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                              {contrato.objeto ?? '-'}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500 flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              Órgão
                            </p>
                            <p className="font-medium text-slate-900 truncate">
                              {contrato.orgaoContratante ?? contrato.orgao_contratante ?? '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500 flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Vigência
                            </p>
                            <p className="font-medium text-slate-900">
                              {(() => {
                                const di = contrato.dataInicio ?? contrato.data_inicio;
                                const df = contrato.dataFim ?? contrato.data_fim;
                                if (!di || !df) return "-";
                                const d1 = safeParseISO(di);
                                const d2 = safeParseISO(df);
                                return (d1 && d2) ? `${format(d1, "dd/MM/yyyy")} - ${format(d2, "dd/MM/yyyy")}` : "-";
                              })()}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500 flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              Valor Total
                            </p>
                            <p className="font-medium text-slate-900">
                              {formatCurrency(contrato.valor ?? contrato.valor_total)}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500">A Faturar</p>
                            <p className="font-medium text-blue-600">
                              {formatCurrency(
                                (contrato.valor ?? contrato.valor_total) - ((contrato.valorFaturado ?? contrato.valor_faturado) || 0)
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      <Link to={createPageUrl(`VisualizarContrato?id=${contrato.id ?? ''}`)}>
                        <Button variant="outline" size="sm">
                          Ver Detalhes
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
